import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';

import './FeatureCarousel.scss';
import AISchedulerLogo from '../../assets/scheduler/ai_scheduler_logo.png';

export type FeatureCarouselItem = {
  title?: string;
  text?: string;
  description?: string;
  icon?: React.ReactNode;
};

type FeatureCarouselProps = {
  items: FeatureCarouselItem[];
  autoPlayIntervalMs?: number;
  className?: string;
};

const FeatureCarousel: React.FC<FeatureCarouselProps> = ({
  items,
  autoPlayIntervalMs = 3200,
  className,
}) => {
  const [index, setIndex] = useState(0);
  const safeItems = useMemo(() => items.filter(Boolean), [items]);

  useEffect(() => {
    if (!safeItems.length) return;
    const id = setInterval(() => {
      setIndex((prev) => (prev + 1) % safeItems.length);
    }, autoPlayIntervalMs);
    return () => clearInterval(id);
  }, [autoPlayIntervalMs, safeItems.length]);

  if (!safeItems.length) return null;
  const current = safeItems[index];

  return (
    <div className={clsx('feature-carousel', className)}>
      <div className="feature-carousel__icon">
        <img src={AISchedulerLogo} alt="AI Scheduler" />
      </div>
      <div className="feature-carousel__text">
        {(current?.text || current?.title) && (
          <div className="feature-carousel__title">
            {current?.text
              ? current.text.split('\n').map((line, idx, arr) => (
                  <React.Fragment key={idx}>
                    {line}
                    {idx < arr.length - 1 && <br />}
                  </React.Fragment>
                ))
              : current?.title}
          </div>
        )}
        {current?.description && (
          <div className="feature-carousel__description">{current.description}</div>
        )}
      </div>
      <div className="feature-carousel__dots">
        {safeItems.map((item, idx) => (
          <button
            key={item.text || item.title || idx}
            className={clsx(
              'feature-carousel__dot',
              idx === index && 'feature-carousel__dot--active'
            )}
            onClick={() => setIndex(idx)}
            aria-label={`Show feature ${idx + 1}`}
            type="button"
          />
        ))}
      </div>
    </div>
  );
};

export default FeatureCarousel;
